<template>
  <!--
    The root <div> stays in place as a stable anchor in the parent's DOM. Only the inner
    portalEl is teleported to #modal-container (see _moveToModalContainer). Moving the root
    itself broke Vue's child diff: when sibling v-if modals swapped, updateChildren used
    nextSibling() of a teleported root as an insertBefore reference, which is not a child
    of the parent -> NotFoundError.
  -->
  <div>
    <div ref="portalEl">
      <!-- Backdrop: declarative @click avoids listener-timing race -->
      <div
        v-if="isVisible && backdropOption && isBackdropVisible"
        class="modal-backdrop"
        :style="backdropStyle"
        data-testid="bs-modal-backdrop"
        @click="handleBackdropClick"
      ></div>

      <div
        v-if="shouldRenderModal"
        :id="modalId"
        ref="modalEl"
        class="modal"
        :class="modalClass"
        :style="modalStyle"
        tabindex="-1"
        :aria-labelledby="`${modalId}-label`"
        :aria-hidden="!isVisible"
        data-testid="bs-modal"
        @click.self="handleBackdropClick"
      >
        <div
          class="modal-dialog"
          :class="dialogClassList"
          :style="dialogStyle"
          role="document"
          data-testid="bs-modal-dialog"
        >
          <div class="modal-content" :class="contentClass">
            <!-- Dim overlay when a child modal is stacked on top -->
            <div v-if="isDimmed" class="modal-dim-overlay"></div>

            <div v-if="!hideHeader" class="modal-header">
              <slot name="modal-header">
                <h5 v-if="title" class="modal-title">{{ title }}</h5>
                <button
                  type="button"
                  class="btn-close"
                  aria-label="Close"
                  @click="hide('headerclose')"
                ></button>
              </slot>
            </div>

            <div class="modal-body" :class="bodyClass">
              <!--
                Gate slot on hasBeenShown: child components are not instantiated
                before the modal first opens (lazy init), but are preserved across
                hides when destroyOnHide=false.
              -->
              <template v-if="hasBeenShown">
                <slot />
              </template>
            </div>

            <div v-if="!hideFooter" class="modal-footer">
              <slot name="modal-footer">
                <button
                  v-if="cancelTitle"
                  type="button"
                  class="btn btn-secondary"
                  @click="hide('cancel')"
                >
                  {{ cancelTitle }}
                </button>
                <button
                  v-if="okTitle"
                  type="button"
                  class="btn btn-primary"
                  :disabled="okDisabled"
                  @click="$emit('ok', $event)"
                >
                  {{ okTitle }}
                </button>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import ModalPkg from 'bootstrap/js/dist/modal';

const Modal = ModalPkg.default || ModalPkg;

// ---------------------------------------------------------------------------
// Reactive modal stack — shared across all b-modal instances
// ---------------------------------------------------------------------------
const BASE_Z_INDEX = 1050;
const Z_STEP = 2;

const stackState = new Vue({
  data() {
    return { stack: [] };
  },
});

export const modalStack = {
  push(id) {
    if (!stackState.stack.includes(id)) stackState.stack.push(id);
  },
  pop(id) {
    const idx = stackState.stack.indexOf(id);
    if (idx !== -1) stackState.stack.splice(idx, 1);
  },
  isTop(id) {
    return stackState.stack[stackState.stack.length - 1] === id;
  },
  getZIndex(id) {
    const idx = stackState.stack.indexOf(id);
    return BASE_Z_INDEX + (idx === -1 ? 0 : idx) * Z_STEP;
  },
  get stack() {
    return stackState.stack;
  },
  getCount() {
    return stackState.stack.length;
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default {
  name: 'b-modal',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    id: { type: String, default: null },

    /** v-model binding — parent is source of truth */
    value: { type: Boolean, default: undefined },

    size: {
      type: String,
      default: '',
      validator: (v) => !v || ['sm', 'md', 'lg', 'xl', 'huge'].includes(v),
    },

    hideFooter: { type: Boolean, default: false },
    hideHeader: { type: Boolean, default: false },
    centered: { type: Boolean, default: false },
    noCloseOnBackdrop: { type: Boolean, default: false },
    noCloseOnEsc: { type: Boolean, default: false },

    modalClass: { type: [String, Array, Object], default: null },
    contentClass: { type: [String, Array, Object], default: null },
    bodyClass: { type: [String, Array, Object], default: null },

    backdrop: { type: [Boolean, String], default: true },
    title: { type: String, default: null },
    okTitle: { type: String, default: 'OK' },
    cancelTitle: { type: String, default: 'Cancel' },
    okDisabled: { type: Boolean, default: false },

    /**
     * destroyOnHide=false (default)
     *   Modal stays in DOM after first open → cheap re-opens, slot state preserved.
     * destroyOnHide=true
     *   Modal is fully unmounted after each hide → matches BV lazy/static=false.
     */
    destroyOnHide: { type: Boolean, default: false },
  },

  data() {
    return {
      bsModal: null,
      modalListenersAttached: false,

      // isHidden=true  → element not in DOM
      // isHidden=false → element in DOM (visible or kept-alive)
      isHidden: true,

      isVisible: false,

      // Stays true after first open so slot children survive hides
      // when destroyOnHide=false.
      hasBeenShown: false,

      isOpening: false,
      isClosing: false,

      // Intent queued during a transition; resolved in onHidden.
      _pendingShow: false,
    };
  },

  computed: {
    modalId() {
      return (this.id != null && String(this.id).length > 0)
        ? this.id
        : `bv-modal-${this._uid}`;
    },

    dialogClassList() {
      const c = [];
      if (this.size === 'sm') c.push('modal-sm');
      if (this.size === 'lg') c.push('modal-lg');
      if (this.size === 'xl') c.push('modal-xl');
      if (this.size === 'huge') c.push('modal-huge');
      if (this.centered) c.push('modal-dialog-centered');
      return c;
    },

    backdropOption() {
      if (this.noCloseOnBackdrop) return 'static';
      if (this.backdrop === false) return false;
      if (this.backdrop === 'static') return 'static';
      return true;
    },

    isDimmed() {
      return !modalStack.isTop(this.modalId) && modalStack.getCount() > 1;
    },
    isBackdropVisible() {
      return modalStack.isTop(this.modalId);
    },

    dialogStyle() { return {}; },

    modalStyle() {
      return { zIndex: modalStack.getZIndex(this.modalId) };
    },
    backdropStyle() {
      return { zIndex: modalStack.getZIndex(this.modalId) - 5 };
    },

    shouldRenderModal() {
      return !this.isHidden;
    },
  },

  watch: {
    value(val) {
      if (val === true) {
        this.$nextTick(() => this.show());
      } else if (val === false) {
        this.$nextTick(() => this.hide());
      }
    },
  },

  mounted() {
    if (this.value === true) {
      this.$nextTick(() => this.show());
    }
  },

  beforeDestroy() {
    if (this.isVisible) {
      modalStack.pop(this.modalId);
      this._cleanupBodyStyles();
    }
    this.unbindModal();

    const container = document.getElementById('modal-container');
    const { portalEl } = this.$refs;
    if (container && portalEl && portalEl.parentNode === container) {
      container.removeChild(portalEl);
    }
  },

  methods: {
    show() {
      if (this.isVisible && !this.isClosing) return;

      if (this.isClosing) {
        this._pendingShow = true;
        return;
      }

      if (this.isOpening) return;

      this.isOpening = true;
      this._pendingShow = false;

      this.isHidden = false;
      this.hasBeenShown = true;

      this.$nextTick(() => {
        this._moveToModalContainer();

        this.$nextTick(() => {
          this._ensureInstance();
          if (this.bsModal) {
            this.bsModal.show();
          } else {
            this.isOpening = false;
            this.isHidden = true;
          }
        });
      });
    },

    hide(trigger = '') {
      if (!this.isVisible || this.isClosing) return;

      this.isClosing = true;
      this.$emit('hide', trigger);

      this._ensureInstance();
      if (this.bsModal) {
        this.bsModal.hide();
      } else {
        this.isClosing = false;
        this._forceHidden();
      }
    },

    _moveToModalContainer() {
      const container = document.getElementById('modal-container');
      const { portalEl } = this.$refs;
      if (container && portalEl && portalEl.parentNode !== container) {
        container.appendChild(portalEl);
      }
    },

    bindModal() {
      const el = this.$refs.modalEl;
      if (!el || typeof Modal.getOrCreateInstance !== 'function') return;

      this.bsModal = Modal.getOrCreateInstance(el, {
        backdrop: false,
        keyboard: !this.noCloseOnEsc,
      });

      if (!this.modalListenersAttached) {
        el.addEventListener('shown.bs.modal', this.onShown);
        el.addEventListener('hide.bs.modal', this._onBsHide);
        el.addEventListener('hidden.bs.modal', this.onHidden);
        this.modalListenersAttached = true;
      }
    },

    _ensureInstance() {
      if (!this.bsModal) this.bindModal();
    },

    unbindModal() {
      const el = this.$refs.modalEl;
      if (el && this.modalListenersAttached) {
        el.removeEventListener('shown.bs.modal', this.onShown);
        el.removeEventListener('hide.bs.modal', this._onBsHide);
        el.removeEventListener('hidden.bs.modal', this.onHidden);
        this.modalListenersAttached = false;
      }
      if (this.bsModal) {
        this.bsModal.dispose();
        this.bsModal = null;
      }
    },

    onShown() {
      this.isOpening = false;
      if (!this.isVisible) {
        this.isVisible = true;
        modalStack.push(this.modalId);
        this.$emit('input', true);
        this.$emit('shown');
      }
    },

    _onBsHide() {
      if (!this.isClosing) {
        this.isClosing = true;
        this.$emit('hide');
      }
    },

    onHidden() {
      this.isClosing = false;

      if (this.isVisible) {
        this._forceHidden();
      }

      if (this._pendingShow) {
        this._pendingShow = false;
        this.$nextTick(() => this.show());
      }
    },

    _forceHidden() {
      this.isVisible = false;
      modalStack.pop(this.modalId);
      this._cleanupBodyStyles();
      this.$emit('input', false);
      this.$emit('hidden');

      if (this.destroyOnHide) {
        this.unbindModal();
        this.hasBeenShown = false;
        this.isHidden = true;
      }
    },

    handleBackdropClick() {
      if (!this.noCloseOnBackdrop && this.backdropOption !== 'static') {
        this.hide('backdrop');
      }
    },

    _cleanupBodyStyles() {
      if (modalStack.getCount() === 0) {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      } else {
        document.body.classList.add('modal-open');
        document.body.style.overflow = 'hidden';
      }
    },
  },
};
</script>

<style>
.modal {
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

.modal-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  opacity: 1 !important;
  display: block !important;
  visibility: visible !important;
  pointer-events: auto !important;
}

.modal-content {
  position: relative;
}

.modal-dim-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100;
  border-radius: inherit;
  pointer-events: all;
}
</style>
